import { useCallback, useEffect, useState } from "react";
import { useSettingsStore } from "@/stores/SettingsStore";

/**
 * Page-local theme for the legacy /headlines view.
 *
 * Legacy portal users expect a light feed, but the app defaults to dark. This
 * hook lets the Legacy View manage its own theme *independently* of the global
 * `SettingsStore.theme` — it toggles the `<html>.dark` class (which Tailwind's
 * `darkMode: 'class'` strategy reads) only while the page is mounted, and
 * restores the user's global theme on unmount. The preference is persisted
 * under its own localStorage key, so a normal user's global theme is NEVER
 * written and other users are unaffected.
 *
 * Defaults to light on first visit.
 */
const STORAGE_KEY = "legacyHeadlinesTheme";
export type LegacyTheme = "light" | "dark";

function readPref(): LegacyTheme {
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function applyTheme(theme: LegacyTheme) {
  const html = document.documentElement;
  if (theme === "dark") html.classList.add("dark");
  else html.classList.remove("dark");
  html.setAttribute("data-theme", theme);
}

export function useLegacyHeadlinesTheme() {
  const [theme, setTheme] = useState<LegacyTheme>(readPref);
  const globalTheme = useSettingsStore((state) => state.theme);

  // Apply the page-local theme while mounted; restore the global theme on
  // unmount (or if the global theme changes underneath us).
  useEffect(() => {
    applyTheme(theme);
    return () => applyTheme(globalTheme === "dark" ? "dark" : "light");
  }, [theme, globalTheme]);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: LegacyTheme = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore persistence failures */
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}
